import { onMounted, onUnmounted, ref, unref, watch, type Ref } from 'vue';

export interface UseStickyOnScrollOptions {
  distance?: number | Ref<number>;

  target?: Ref<HTMLElement | null>;
}

export interface UseStickyOnScroll {
  collapsed: Ref<boolean>;

  progress: Ref<number>;
}

const FALLBACK_DISTANCE = 1;

export function useStickyOnScroll(options: UseStickyOnScrollOptions = {}): UseStickyOnScroll {
  const { distance, target } = options;

  const collapsed = ref(false);
  const progress = ref(0);

  if (typeof window === 'undefined') {
    return { collapsed, progress };
  }

  const raf: (cb: FrameRequestCallback) => number =
    window?.requestAnimationFrame?.bind(window) ?? ((cb) => window.setTimeout(() => cb(0), 16));
  const caf: (id: number) => void = window?.cancelAnimationFrame?.bind(window) ?? window?.clearTimeout?.bind(window);

  const readScrollY = (): number => window.scrollY ?? window.pageYOffset ?? document.documentElement?.scrollTop ?? 0;

  const readDistance = (): number => {
    const measured = target?.value?.offsetHeight;
    if (measured && measured > 0) {
      return measured;
    }
    const configured = unref(distance);
    return typeof configured === 'number' && configured > 0 ? configured : FALLBACK_DISTANCE;
  };

  const update = (): void => {
    const d = Math.max(readDistance(), 1);
    const y = readScrollY();
    progress.value = Math.min(Math.max(y / d, 0), 1);
    collapsed.value = y >= d;
  };

  let ticking = false;
  let rafId = 0;
  const requestTick = (): void => {
    if (ticking) {
      return;
    }
    ticking = true;
    rafId = raf(() => {
      ticking = false;
      update();
    });
  };

  onMounted(() => {
    window.addEventListener('scroll', requestTick, { passive: true });
    window.addEventListener('resize', requestTick, { passive: true });
    update();
  });

  onUnmounted(() => {
    window.removeEventListener('scroll', requestTick);
    window.removeEventListener('resize', requestTick);
    if (rafId) {
      caf(rafId);
    }
  });

  // Пересчёт при смене конфигурируемой дистанции, переданной как ref.
  if (distance && typeof distance !== 'number') {
    watch(distance, requestTick);
  }

  return { collapsed, progress };
}
